import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import SchoolDocumentHeader, { schoolLogoFor } from "@/components/SchoolDocumentHeader";
import PrintSignatures from "@/components/PrintSignatures";
import { evaluationPeriodText } from "@/lib/evaluationPeriod";
import { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { staffTitles } from "@/lib/staffTitles";

export default function PrincipalEvaluationPrint({ entry, options, onFinish }) {
  const container = useRef(null);
  useEffect(() => {
    if (!entry) return undefined;
    let cancelled = false;
    const cleanup = () => {
      document.body.classList.remove("printing-principal-evaluation");
      onFinish();
    };
    window.addEventListener("afterprint", cleanup);
    const start = async () => {
      try {
        if (!schoolLogoFor(entry.school_key)) throw new Error("لا يمكن الطباعة قبل تحديد المدرسة.");
        await Promise.all([...container.current.querySelectorAll("img")].map((image) => image.decode()));
        if (document.fonts?.ready) await document.fonts.ready;
        if (cancelled) return;
        document.body.classList.add("printing-principal-evaluation");
        window.print();
      } catch (error) {
        if (!cancelled) { toast.error(formatErr(error)); cleanup(); }
      }
    };
    start();
    return () => {
      cancelled = true;
      window.removeEventListener("afterprint", cleanup);
      document.body.classList.remove("printing-principal-evaluation");
    };
  }, [entry, onFinish]);
  if (!entry) return null;
  return createPortal(
    <article className="principal-evaluation-print" ref={container} dir="rtl" data-testid="evaluation-print-document">
      <style>{"@media print { @page { size: A4 landscape; margin: 8mm; } }"}</style>
      <SchoolDocumentHeader schoolKey={entry.school_key} gender={entry.gender} schoolName={entry.school_name} />
      <h1>تقييم أداء {staffTitles(entry.gender).counselor}</h1>
      <p>{staffTitles(entry.gender).counselor}: {entry.counselor_name}</p>
      <p>فترة التقييم: {evaluationPeriodText(entry.period)}</p>
      <p>تاريخ إعداد التقييم: {new Date(entry.created_at).toLocaleDateString("ar-SA")}</p>
      {entry.updated_at && <p>آخر تعديل: {new Date(entry.updated_at).toLocaleDateString("ar-SA")}</p>}
      <table>
        <thead><tr><th>المعيار</th><th>التقييم</th></tr></thead>
        <tbody>{Object.entries(entry.ratings).map(([key, value]) => <tr key={key}><td>{options.criteria[key]}</td><td>{options.choices[value]}</td></tr>)}</tbody>
      </table>
      <h2>ملاحظات {staffTitles(entry.principal_gender || entry.gender).principal}</h2>
      <p className="evaluation-print-notes">{entry.notes || "لا توجد ملاحظات."}</p>
      <PrintSignatures counselorName={entry.counselor_name} principalName={entry.principal_name} counselorGender={entry.gender} principalGender={entry.principal_gender} />
    </article>,
    document.body,
  );
}
